import {HalImage} from "@cm/hal"
import {Box2, Box2Like, Vector2Like} from "@cm/math"
import {FetchArgs, ImageOpDataObjectPageSupplier} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-page-supplier"
import {DataObjectImageDescriptor} from "@app/textures/service/textures-api.service"
import {ImageDataType} from "@generated"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {ImageOpDataObjectManagerCallback} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object-manager-callback"
import {RetiledImage} from "@cm/browser-utils"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"

export class ImageOpDataObjectPageSupplierPaged implements ImageOpDataObjectPageSupplier {
    constructor(
        private callback: ImageOpDataObjectManagerCallback,
        private descriptor: DataObjectImageDescriptor,
    ) {
        if (!this.descriptor.imageTilesDescriptor) {
            throw new Error("Descriptor does not contain imageTilesDescriptor")
        }
        this.isSrgb = this.descriptor.imageDataType === ImageDataType.Color
        this.pendingTiledImage = this.callback.loadTiledImage({
            ...this.descriptor.imageTilesDescriptor,
            tileChannels: this.descriptor.imageTilesDescriptor.tileChannels ?? 3,
        })
    }

    async fetch(args: FetchArgs) {
        this._tiledImage = await this.pendingTiledImage
        const tiledImage = this.tiledImage
        const mipLevel = args.mipLevelRegion.mipLevel ?? 0
        if (mipLevel >= tiledImage.numImageMipLevels) {
            throw new Error(`Requested mip level ${mipLevel} exceeds maximum mip level ${tiledImage.numImageMipLevels - 1}`)
        }
        const pages = await this.computePages(mipLevel, args.mipLevelRegion)
        // we want to decode to a temporary image first, then blit to the target to avoid partial writes into the target (as this can cause rendering errors when drawing half-updated images)
        const temporaryTarget = this.callback.ctx.createImagePhysical({
            ...args.target.descriptor,
            numMipLevels: 1,
            numLayers: 1,
            width: args.mipLevelRegion.width,
            height: args.mipLevelRegion.height,
        })
        await Promise.all(
            pages.map(async (pageDesc) => {
                const targetOffset = {
                    x: pageDesc.pageIndex.x * tiledImage.tileSize.width - args.mipLevelRegion.x,
                    y: pageDesc.pageIndex.y * tiledImage.tileSize.height - args.mipLevelRegion.y,
                }
                const priority = 0
                return this.writePageToHalImage(pageDesc, temporaryTarget.ref, targetOffset, 0, priority, args.options?.abortSignal)
            }),
        )
        this.callback.ctx.blit({
            sourceImage: temporaryTarget.ref,
            targetImage: args.target,
            targetOffset: {
                x: args.mipLevelRegion.x,
                y: args.mipLevelRegion.y,
            },
            targetMipLevel: mipLevel,
        })
        temporaryTarget.release()
    }

    private async computePages(mipLevel: number, region?: Box2Like) {
        const fullMipLevelRegion = {
            ...getMipLevelSize(this.descriptor, mipLevel),
            x: 0,
            y: 0,
        }
        const clippedRegion = region ? Box2.intersect(fullMipLevelRegion, region) : fullMipLevelRegion
        if (mipLevel >= this.tiledImage.numImageMipLevels) {
            throw new Error(`Requested mip level ${mipLevel} exceeds maximum mip level ${this.tiledImage.numImageMipLevels - 1}`)
        }
        const tileSize = this.tiledImage.tileSize
        const minPageX = Math.floor(clippedRegion.x / tileSize.width)
        const maxPageX = Math.floor((clippedRegion.x + clippedRegion.width - 1) / tileSize.width)
        const minPageY = Math.floor(clippedRegion.y / tileSize.height)
        const maxPageY = Math.floor((clippedRegion.y + clippedRegion.height - 1) / tileSize.height)
        const pages: PageDesc[] = []
        for (let pageY = minPageY; pageY <= maxPageY; pageY++) {
            for (let pageX = minPageX; pageX <= maxPageX; pageX++) {
                const pageIndex = {x: pageX, y: pageY}
                const pageHash = this.computePageHash(pageIndex, mipLevel)
                pages.push({
                    pageHash,
                    pageIndex,
                    mipLevel,
                })
            }
        }
        return pages
    }

    private async writePageToHalImage(
        pageDesc: PageDesc,
        target: HalImage,
        targetOffset: Vector2Like,
        targetMipLevel: number,
        priority: number,
        abortSignal?: AbortSignal,
    ) {
        const pageSource = await this.tiledImage.createTileImage(pageDesc.mipLevel, pageDesc.pageIndex, {priority, abortSignal})
        await target.writeImageData({
            sourceData: {
                data: pageSource.data,
                isSrgb: this.isSrgb,
            },
            sourceRegion: pageSource.region,
            targetOffset,
            targetMipLevel,
            maxPixelsPerUpload: TextureEditorSettings.MaxPixelsPerUpload,
        })
        pageSource.release()
    }

    private computePageHash(pageIndex: Vector2Like, mipLevel: number): number {
        if (pageIndex.x < 0 || pageIndex.x > 0xfff) {
            throw new Error(`pageX ${pageIndex.x} out of bounds [0, 4095]`)
        }
        if (pageIndex.y < 0 || pageIndex.y > 0xfff) {
            throw new Error(`pageY ${pageIndex.y} out of bounds [0, 4095]`)
        }
        if (mipLevel < 0 || mipLevel > 0xff) {
            throw new Error(`mipLevel ${mipLevel} out of bounds [0, 255]`)
        }
        return (mipLevel << 24) | (pageIndex.y << 12) | pageIndex.x
    }

    private get tiledImage(): RetiledImage {
        if (!this._tiledImage) {
            throw new Error("Tiled image not loaded yet")
        }
        return this._tiledImage
    }

    private readonly isSrgb: boolean
    private pendingTiledImage: Promise<RetiledImage>
    private _tiledImage: RetiledImage | null = null
}

type PageDesc = {
    pageHash: number
    pageIndex: Vector2Like
    mipLevel: number
}
